import { useUnit } from 'effector-react';
import { ArrowLeft, Mail, MailCheck } from 'lucide-react';
import { useState, type SyntheticEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { $signInMethods } from '../../entities/session';
import { authApi } from '../../shared/api/auth';
import { routes } from '../../shared/routing';
import { Button, Input } from '../../shared/ui';
import {
  $error,
  $returnTo,
  $sentTo,
  differentEmailRequested,
  emailSubmitted,
  requestLinkFx,
} from './model';

const ERROR_KEYS = [
  'rate_limited',
  'bad_request',
  'link_invalid',
  'link_expired',
  'link_other_browser',
  'oauth_failed',
  'email_unverified',
  'provider_unavailable',
  'email_unavailable',
] as const;
type ErrorKey = (typeof ERROR_KEYS)[number];
const errorKey = (code: string): ErrorKey | 'generic' =>
  (ERROR_KEYS as readonly string[]).includes(code) ? (code as ErrorKey) : 'generic';

/** Google's "G" in the product's text colour (brand marks are not recoloured elsewhere). */
function GoogleMark() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden className="size-4">
      <path
        fill="currentColor"
        d="M21.6 12.23c0-.71-.06-1.4-.18-2.05H12v3.87h5.39a4.6 4.6 0 0 1-2 3.02v2.5h3.23c1.9-1.74 2.98-4.3 2.98-7.34ZM12 22c2.7 0 4.96-.9 6.62-2.43l-3.23-2.5c-.9.6-2.04.96-3.39.96-2.6 0-4.81-1.76-5.6-4.12H3.07v2.58A10 10 0 0 0 12 22Zm-5.6-8.1a6 6 0 0 1 0-3.8V7.52H3.07a10 10 0 0 0 0 8.96L6.4 13.9ZM12 5.98c1.47 0 2.79.5 3.83 1.5l2.87-2.87A9.63 9.63 0 0 0 12 2a10 10 0 0 0-8.93 5.52L6.4 10.1C7.19 7.74 9.4 5.98 12 5.98Z"
      />
    </svg>
  );
}

function GitHubMark() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden className="size-4">
      <path
        fill="currentColor"
        d="M12 2a10 10 0 0 0-3.16 19.49c.5.09.68-.22.68-.48v-1.7c-2.78.6-3.37-1.34-3.37-1.34-.45-1.16-1.1-1.46-1.1-1.46-.91-.62.07-.61.07-.61 1 .07 1.53 1.03 1.53 1.03.9 1.52 2.34 1.08 2.91.83.09-.65.35-1.08.63-1.33-2.22-.25-4.55-1.11-4.55-4.94 0-1.09.39-1.98 1.03-2.68-.1-.25-.45-1.27.1-2.64 0 0 .84-.27 2.75 1.02a9.58 9.58 0 0 1 5 0c1.91-1.3 2.75-1.02 2.75-1.02.55 1.37.2 2.39.1 2.64.64.7 1.03 1.59 1.03 2.68 0 3.84-2.34 4.68-4.57 4.93.36.31.68.92.68 1.85v2.75c0 .27.18.58.69.48A10 10 0 0 0 12 2Z"
      />
    </svg>
  );
}

export function SignInPage() {
  const { t } = useTranslation();
  const [methods, error, sentTo, returnTo, sending, submit, retry, home] = useUnit([
    $signInMethods,
    $error,
    $sentTo,
    $returnTo,
    requestLinkFx.pending,
    emailSubmitted,
    differentEmailRequested,
    routes.home.open,
  ]);
  const [email, setEmail] = useState('');
  const onSubmit = (e: SyntheticEvent<HTMLFormElement>) => {
    e.preventDefault();
    submit(email.trim());
  };
  const oauth = (methods ?? []).filter((m) => m !== 'email');

  return (
    <main className="flex h-full items-center justify-center overflow-y-auto bg-bg-0 p-4">
      <div className="w-full max-w-sm space-y-5 rounded-xl border border-border-subtle bg-bg-1 p-6 shadow-popover">
        <div className="space-y-1.5">
          <img src="/favicon.svg" alt="" className="size-7" />
          <h1 className="text-lg font-semibold">
            {sentTo ? t('signIn.checkTitle') : t('signIn.title')}
          </h1>
          <p className="text-sm text-text-3">
            {sentTo ? t('signIn.checkBody', { email: sentTo }) : t('signIn.body')}
          </p>
        </div>

        {error && (
          <p
            role="alert"
            className="rounded-md border border-negative/40 bg-negative/10 px-3 py-2 text-sm text-text-1"
          >
            {t(`signIn.errors.${errorKey(error)}`)}
          </p>
        )}

        {sentTo ? (
          <div className="space-y-3">
            <MailCheck className="size-8 text-accent" aria-hidden />
            <Button variant="ghost" onClick={retry}>
              {t('signIn.useDifferent')}
            </Button>
          </div>
        ) : methods !== null && methods.length === 0 ? (
          <p className="text-sm text-text-2">{t('signIn.unavailable')}</p>
        ) : (
          <div className="space-y-3">
            {oauth.map((provider) => (
              <Button
                key={provider}
                className="w-full"
                leadingIcon={provider === 'google' ? <GoogleMark /> : <GitHubMark />}
                onClick={() => {
                  window.location.assign(authApi.oauthStartUrl(provider, returnTo));
                }}
              >
                {t(`signIn.${provider}`)}
              </Button>
            ))}
            {oauth.length > 0 && methods?.includes('email') && (
              <div className="flex items-center gap-3 text-xs text-text-3" aria-hidden>
                <span className="h-px flex-1 bg-border-subtle" />
                {t('signIn.or')}
                <span className="h-px flex-1 bg-border-subtle" />
              </div>
            )}
            {methods?.includes('email') && (
              <form onSubmit={onSubmit} className="space-y-2">
                <label htmlFor="signin-email" className="block text-xs text-text-3">
                  {t('signIn.email')}
                </label>
                <Input
                  id="signin-email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                  }}
                />
                <Button
                  type="submit"
                  variant="primary"
                  className="w-full"
                  leadingIcon={<Mail />}
                  disabled={sending}
                >
                  {sending ? t('signIn.sending') : t('signIn.sendLink')}
                </Button>
              </form>
            )}
          </div>
        )}

        <Button
          variant="ghost"
          size="sm"
          leadingIcon={<ArrowLeft />}
          onClick={() => {
            void home();
          }}
        >
          {t('signIn.back')}
        </Button>
      </div>
    </main>
  );
}
